import { useMemo } from 'react';

type BaseEnhancedItem = {
  isDisabled: boolean;
  isSelected: boolean;
  label: string;
  value: string;
  groupLabel?: string;
};

export type EnhancedItem<Item> =
  | (BaseEnhancedItem & {
      itemType: 'regular';
      item: Item;
    })
  | (BaseEnhancedItem & {
      itemType: 'selectAll';
      item: string;
      isIndeterminate: boolean;
    })
  | (BaseEnhancedItem & {
      itemType: 'groupLabel';
      item: string;
      isIndeterminate: boolean;
    });

type UseEnhancedItemsProps<Item> = {
  items: Item[];
  value: string | string[] | null | undefined;
  showSelectAllItem?: boolean;
  /**
   * Group items by the returned string. Returning `undefined` marks the item
   * as ungrouped - it is rendered as a regular item without a group header.
   *
   * Ungrouped items keep their relative order and are placed right after the
   * group block they followed in `items`. Groups are never split. Ungrouped
   * items that appear before any group are placed at the end of the list.
   */
  groupBy?: (item: Item) => string | undefined;
  itemToStringValue: (item: Item) => string;
  itemToStringLabel: (item: Item) => string;
  isItemDisabled?: (item: Item) => boolean;
  isGroupDisabled?: (groupLabel: string) => boolean;
};

export const useEnhancedItems = <Item>({
  items,
  value,
  showSelectAllItem,
  groupBy,
  itemToStringValue,
  itemToStringLabel,
  isItemDisabled,
  isGroupDisabled,
}: UseEnhancedItemsProps<Item>): EnhancedItem<Item>[] => {
  return useMemo(() => {
    const enhancedItems: EnhancedItem<Item>[] = [];

    // Normalize value to array for consistent checking
    const valueArray = Array.isArray(value)
      ? value
      : value == null
      ? []
      : [value];

    // Helper function to check if an item is selected
    const checkIfItemIsSelected = (itemValue: string): boolean => {
      return valueArray.includes(itemValue);
    };

    // Helper function to push a regular enhanced item. `groupLabel` is set
    // only when the item belongs to a group; ungrouped items omit it.
    // `extraDisabled` ORs in an additional disabled source (e.g. group-level
    // disable) on top of the per-item disabled check.
    const pushRegularItem = (
      item: Item,
      groupLabel?: string,
      extraDisabled = false
    ): void => {
      const itemValue = itemToStringValue(item);
      enhancedItems.push({
        ...(groupLabel !== undefined ? { groupLabel } : {}),
        isDisabled: extraDisabled || (isItemDisabled?.(item) ?? false),
        isSelected: checkIfItemIsSelected(itemValue),
        value: itemValue,
        label: itemToStringLabel(item),
        itemType: 'regular',
        item,
      });
    };

    if (showSelectAllItem && items.length > 0) {
      // Calculate enabled items for Select All state
      const enabledItems = items.filter((item) => {
        const groupLabel = groupBy?.(item);
        const isGroupDisabledValue =
          groupLabel && isGroupDisabled ? isGroupDisabled(groupLabel) : false;
        const isItemDisabledValue = isItemDisabled?.(item) ?? false;
        return !isGroupDisabledValue && !isItemDisabledValue;
      });

      const selectedEnabledItems = enabledItems.filter((item) =>
        checkIfItemIsSelected(itemToStringValue(item))
      );

      const someEnabledItemsAreSelected = selectedEnabledItems.length > 0;
      const allEnabledItemsAreSelected =
        selectedEnabledItems.length === enabledItems.length;

      enhancedItems.push({
        label: 'Select All',
        value: 'Select All',
        isDisabled: false,
        isSelected: someEnabledItemsAreSelected,
        isIndeterminate:
          someEnabledItemsAreSelected && !allEnabledItemsAreSelected,
        itemType: 'selectAll',
        item: 'Select All',
      });
    }

    // Check if we are doing grouping
    if (groupBy) {
      // We bucket items into their groups first so that groups are never split
      // apart in the output. Group buckets are kept in the order they are
      // first encountered in `items`, preserving the user's intended group
      // ordering.
      //
      // Ungrouped items (groupBy returns undefined) are attached to the group
      // block they followed in `items`, so they appear just after that group's
      // items. This gives "semi-original" order: relative order among
      // ungrouped items is preserved, and they stay near the group they were
      // interleaved with - but they never fragment a group's items.
      // Ungrouped items seen before any group are appended at the end as a
      // single trailing ungrouped section (no header).
      const groupBuckets = new Map<string, Item[]>();
      const ungroupedByGroup = new Map<string | null, Item[]>();
      let currentGroup: string | null = null;

      items.forEach((item) => {
        const groupLabel = groupBy(item);

        if (groupLabel === undefined) {
          const bucketKey = currentGroup;
          const bucket = ungroupedByGroup.get(bucketKey) ?? [];
          bucket.push(item);
          ungroupedByGroup.set(bucketKey, bucket);
          return;
        }

        let bucket = groupBuckets.get(groupLabel);
        if (!bucket) {
          bucket = [];
          groupBuckets.set(groupLabel, bucket);
        }
        bucket.push(item);
        currentGroup = groupLabel;
      });

      // Emit each group block (header + items + trailing ungrouped attached to it).
      groupBuckets.forEach((groupItems, groupLabel) => {
        const isGroupDisabledValue = isGroupDisabled?.(groupLabel) ?? false;
        const enabledGroupItems = groupItems.filter(
          (it) => !isItemDisabled?.(it)
        );
        const selectedGroupItems = enabledGroupItems.filter((it) =>
          checkIfItemIsSelected(itemToStringValue(it))
        );
        const allEnabledGroupItemsAreSelected =
          selectedGroupItems.length === enabledGroupItems.length;
        const someEnabledGroupItemsAreSelected = selectedGroupItems.length > 0;
        const allChildrenDisabled = enabledGroupItems.length === 0;

        enhancedItems.push({
          groupLabel,
          isDisabled: isGroupDisabledValue || allChildrenDisabled,
          // For single-select (Autocomplete), group headers are never selected
          isSelected: Array.isArray(value)
            ? someEnabledGroupItemsAreSelected
            : false,
          isIndeterminate: Array.isArray(value)
            ? someEnabledGroupItemsAreSelected &&
              !allEnabledGroupItemsAreSelected
            : false,
          label: groupLabel,
          value: groupLabel,
          itemType: 'groupLabel',
          item: groupLabel,
        });

        groupItems.forEach((item) => {
          pushRegularItem(item, groupLabel, isGroupDisabledValue);
        });

        // Append ungrouped items that were attached to this group block,
        // preserving their original relative order from the `items` array.
        const trailing = ungroupedByGroup.get(groupLabel);
        if (trailing && trailing.length > 0) {
          trailing.forEach((item) => {
            pushRegularItem(item);
          });
        }
      });

      // Emit any ungrouped items that appeared before the first group as a
      // single trailing section (no header).
      const leadingUngrouped = ungroupedByGroup.get(null);
      if (leadingUngrouped && leadingUngrouped.length > 0) {
        leadingUngrouped.forEach((item) => {
          pushRegularItem(item);
        });
      }
    } else {
      // No grouping - just loop through items as is
      items.forEach((item) => {
        pushRegularItem(item);
      });
    }

    return enhancedItems;
  }, [
    items,
    value,
    showSelectAllItem,
    groupBy,
    itemToStringValue,
    itemToStringLabel,
    isItemDisabled,
    isGroupDisabled,
  ]);
};
